import React, { useEffect, useRef, useState, FC } from 'react'
import { t, Trans } from '@lingui/macro'
import { RouteComponentProps } from 'react-router-dom'
import classNames from 'classnames'
import { ChooseElement } from '../topic-detail-page/components/choose-element/ChooseElement'
import TopbarEdit from '../topic-detail-page/components/topbar/TopbarEdit'
import { EditElement } from './EditElement'
import ManageScormPage from '../manage-scorm-page/ManageScormPage'
import MultipageNavPanel from './MultipageNavPanel'
import { EditTitleAndSummary } from './EditTitleAndSummary'

import { apolloClientPrivate } from '../../graphql/apolloClient'
import ErrorMessage from '../../components/common/error/ErrorMessage'

import useSetInterval from '../../app/hooks/useSetInterval'

import {
  Box,
  Modal,
  ButtonDanger,
  ButtonInfo,
  FullBlockSpinner
} from 'front-end/src/components'
import { Operation } from 'apollo-link'

import PowerPointImportModal from './components/PowerPointImportModal'
import ReactQuill from '@capaj/react-quill'
import { useDebouncedCallback } from 'use-debounce'
import { ManageVideoAssessmentPage } from '../manage-video-assessment-page/ManageVideoAssessmentPage'
import {
  TopicModelAsEditor,
  TopicVersionType
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  TopicForEditingQuery,
  useTopicForEditingQuery,
  usePatchElementContentMutation,
  useEndTopicEditingMutation,
  useFetchTopicToStartEditingLazyQuery
} from './TopicEditPage.codegen'
import NotFound from '../../components/common/not-found/NotFound'
import { usePublicApiSession } from '../../public-api/hooks/usePublicApiSession'
import { videoAssessmentTopicVersionTypesForFE } from '../topic-detail-page/StudentTopicDetail'
import { useParams } from 'react-router'
import { TopicEditingContext } from './TopicEditContext'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'

import './TopicEditPage.less'
interface IParamProps {
  topicId: string
  feedbackId?: string
}
export type TopicModelAsEditorWithFeedbacksCount = TopicModelAsEditor &
  TopicForEditingQuery['topic']

export function useTopicForEditQuery() {
  const { topicId } = useParams<{ topicId: string }>()

  return useTopicForEditingQuery({
    variables: {
      topicId: +topicId
    },
    fetchPolicy: 'cache-first'
  })
}

export const TopicEditPageQuery = (props: RouteComponentProps<IParamProps>) => {
  const { data, loading } = useTopicForEditQuery()
  const [queryResult, setQueryResult] = useState(data)

  useEffect(() => {
    apolloClientPrivate['lastFailedOperation'] = null // we need to clean this in case there was an error earlier somewhere in the whole app
    setQueryResult(data)
  }, [data])

  if (loading) {
    return <FullBlockSpinner />
  }

  if (!data || !queryResult) {
    return null
  }

  if (!data.topic) {
    return (
      <ErrorMessage
        message={t`Resource was not found.`}
        includeLinkToDashboard
      />
    )
  }

  return <TopicEditPage queryResult={data.topic} {...props} />
}

const TopicEditPage: FC<
  {
    queryResult: NonNullable<TopicForEditingQuery['topic']>
  } & RouteComponentProps<IParamProps>
> = (props) => {
  const [topic, setTopic] = useState<TopicModelAsEditorWithFeedbacksCount>(
    props.queryResult as TopicModelAsEditorWithFeedbacksCount
  )
  const { theme } = useLooopTheme()
  const [powerPointImportModalVisible, setPowerPointImportModalVisible] =
    useState(false)

  // This quill is used in PowerPointImportModal to sanitize HTML input
  // It simply receives HTML using clipboard API, sanitizes that and then we get that sanitized HTML from it.
  const sanitizationQuillEditorRef = useRef<ReactQuill>(null)

  // TODO (Follow up): Merge these two components into one.
  useEffect(() => {
    setTopic(props.queryResult as TopicModelAsEditorWithFeedbacksCount)
  }, [props.queryResult])

  const gqlOptions = {
    variables: {
      topicId: topic.id
    }
  }
  const { refetch: fetchTopic } = useTopicForEditQuery()

  const [patchElement] = usePatchElementContentMutation() // when user opens editing
  const [endEditing] = useEndTopicEditingMutation(gqlOptions) // when user opens editing
  const [refreshTopic] = useFetchTopicToStartEditingLazyQuery(gqlOptions) // when user is editing for longer time

  const [apolloMutationError, setApolloMutationError] =
    useState<null | Operation>(null)

  useSetInterval(() => refreshTopic(), 100 * 1000) // this ensures that the topic_editors table row is kept up to date eve if a user has it opened without touching it for more than 2 minutes

  useSetInterval(() => {
    const lastFailedOperation: Operation | null =
      apolloClientPrivate['lastFailedOperation']
    // hacky, but from what I tried seems like the easiest way to check globally for mutation errors
    if (lastFailedOperation !== apolloMutationError) {
      // @ts-expect-error
      if (lastFailedOperation?.query.definitions[0]?.operation === 'mutation') {
        setApolloMutationError(apolloClientPrivate['lastFailedOperation'])
      }
    }
  }, 250)

  const debouncedUpdateElement = useDebouncedCallback(
    async (elementId: number, elementPatch: any) => {
      await patchElement({
        variables: {
          topicId: topic.id,
          elementId,
          content: elementPatch.data.content
        }
      })
      await fetchTopic()
    },
    500
  )

  useEffect(() => {
    return () => {
      debouncedUpdateElement.flush() // todo would be nice to be able to do .then here and end editing after flush is done
      endEditing()
    }
  }, [])

  const [currentElementIndex, setCurrentElementIndex] = useState<number | null>(
    null
  )

  const { session } = usePublicApiSession()
  const user = session?.user

  const { draftVersion, currentTopicEditorUser } =
    topic as any as TopicModelAsEditor
  if (currentTopicEditorUser && currentTopicEditorUser.id != user?.id) {
    return (
      <ErrorMessage
        message={t`User ${
          currentTopicEditorUser.email ?? currentTopicEditorUser.display_name
        } is editing the resource at this moment`}
      />
    )
  }

  if (!draftVersion) {
    // can happen when user tries to edit a topic to which he has no editing access
    return (
      <ErrorMessage
        message={t`You don't have editing access to this resource. Contact an org admin to grant you editing access.`}
      />
    )
  }

  if (topic.deleted_at) {
    return <NotFound message={t`This resource was archived.`} />
  }

  const currentElement =
    typeof currentElementIndex === 'number' && currentElementIndex >= 0
      ? draftVersion?.elements[currentElementIndex]
      : null

  const isSinglePage = draftVersion.topic_type === TopicVersionType.s
  const isMultiPage = draftVersion.topic_type === TopicVersionType.m
  const isScormPage = draftVersion.topic_type === TopicVersionType.scorm
  const isVideoAssessment =
    draftVersion?.topic_type &&
    videoAssessmentTopicVersionTypesForFE.includes(draftVersion?.topic_type)

  const { elements } = draftVersion

  const renderElementsOrCurrentElement = () => {
    if (isMultiPage) {
      const { type } = currentElement || {}
      const typeDashed = type?.toLowerCase()

      return (
        <div>
          {currentElement ? (
            <div className={`page page-${typeDashed}`}>
              <div
                className={`ql-editor page-content page-content-${typeDashed} element-${typeDashed} element-mode-view`}
              >
                <EditElement
                  isMultiPage={isMultiPage}
                  element={currentElement}
                  topic={topic}
                />
              </div>
            </div>
          ) : (
            <div className="page page-system-generated-title">
              <EditTitleAndSummary />
            </div>
          )}
        </div>
      )
    }

    return elements.map((element) => {
      const { type, id } = element
      const typeDashed = type.toLowerCase()

      return (
        <div className={`page page-${typeDashed}`} key={id}>
          <Box
            border="1px solid transparent"
            _hover={{
              border: `1px solid ${theme.colors.buttonPrimary[50]}`
            }}
            _focusVisible={{
              boxShadow: 'var(--chakra-shadows-focus)',
              outline: 'none'
            }}
            className={`page-content page-content-${typeDashed} element-wrap element-${typeDashed} element-mode-view`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const element = e.target as HTMLElement
                const editor =
                  element?.querySelector('.ql-editor') ||
                  element?.querySelector('.element-quiz')
                if (editor && editor instanceof HTMLElement) {
                  editor.click()
                  e.preventDefault()
                }
              }
            }}
          >
            <EditElement
              isMultiPage={isMultiPage}
              element={element}
              topic={topic}
            />
          </Box>
        </div>
      )
    })
  }

  return (
    <>
      <div
        className={classNames('panel topic-mode-edit', {
          'topic-view-single': isSinglePage,
          'topic-view-multi': isMultiPage,
          'topic-view-scorm': isScormPage,
          'topic-view-video-assessment': isVideoAssessment
        })}
      >
        <TopicEditingContext.Provider
          value={{
            updateElement: debouncedUpdateElement,
            setPowerPointImportModalVisible,
            setTopic,
            topic,
            setCurrentElementIndex
          }}
        >
          <TopbarEdit />
          <div className="panel-content">
            <div
              className={classNames('pages-wrapper', {
                'full-wrapper': isScormPage || isVideoAssessment
              })}
            >
              {isSinglePage && (
                <div className="page page-system-generated-title">
                  <EditTitleAndSummary />
                </div>
              )}
              {isScormPage ? (
                <ManageScormPage topic={topic} fetchTopic={fetchTopic} />
              ) : (
                renderElementsOrCurrentElement()
              )}
              {isVideoAssessment && (
                <ManageVideoAssessmentPage
                  topic={topic}
                  fetchTopic={fetchTopic}
                />
              )}
            </div>
            {isSinglePage && (
              <>
                <div className="choose-page-wrapper">
                  <ChooseElement />
                </div>
              </>
            )}
            {isMultiPage && (
              <MultipageNavPanel
                currentElement={currentElement}
                selectElement={setCurrentElementIndex}
              />
            )}

            <ReactQuill
              ref={sanitizationQuillEditorRef}
              value={''}
              style={{ display: 'none' }}
            />

            <PowerPointImportModal
              isShown={powerPointImportModalVisible}
              sanitizationQuillEditorRef={sanitizationQuillEditorRef}
              onModalClose={() => {
                setPowerPointImportModalVisible(false)
              }}
            />
          </div>
        </TopicEditingContext.Provider>
      </div>
      <Modal
        header={t`There was a problem`}
        className="TopicEditPage__mutationError"
        subHeader={t`An error occured when saving your changes. If you want to continue editing, you may need to reload the page.`}
        show={apolloMutationError?.operationName === 'patchElementContent'} // the other mutations here like adding a new element and publishing do their own refetch, so they are safe to let fail without hiding the whole page
        cannotBeHidden={true}
        controls={
          <>
            <ButtonDanger
              onClick={() => {
                location.reload()
              }}
            >
              <Trans>Reload whole page</Trans>
            </ButtonDanger>
            <ButtonInfo
              onClick={async () => {
                if (!apolloMutationError) {
                  return
                }
                await apolloClientPrivate.mutate({
                  mutation: apolloMutationError.query,
                  variables: apolloMutationError.variables
                })
                apolloClientPrivate['lastFailedOperation'] = null
                setApolloMutationError(null)
              }}
            >
              <Trans>Retry saving</Trans>
            </ButtonInfo>
          </>
        }
      />
    </>
  )
}
